import { FaAsterisk } from "@react-icons/all-files/fa/FaAsterisk";
import moment from "moment";
import { ChangeEvent, FormEvent, useRef, useState } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap"
import { StatesAlpha2List } from "../statesAlpha2List";


export const AMS_TEXT = 'beim AMS gemeldet';

export type Arbeit = {
  /** USP-ID der Beschäftigung (b_id): gesetzt = Update, fehlt = Neuanlage.
   * Verhindert doppelte Datensätze beim BMIMI-Import. */
  id?: number;
    art: string,
    organisation: string,
    taetigkeit: string,
    land: string,
    von: Date,
    bis?: Date
}

export type AddArbeitComponentProps = {
    callbackOnAdd: (arbeit: Arbeit) => void,
    callbackAbort: () => void
}

export default function AddArbeitComponent(props: AddArbeitComponentProps) {

    const [validated, setValidated] = useState(false);
    const [fromInvalidMessage, setFromInvalidMessage] = useState("");
    const [toInvalidMessage, setToInvalidMessage] = useState("");
    const [artInvalidMessage, setArtInvalidMessage] = useState("");
    const [landInvalidity, setLandInvalidity] = useState("");



    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        if (form.checkValidity() === true) {

            let correct = true;
            const vonDate = moment(form.von.value);
            if (!vonDate.isValid()) {
                setFromInvalidMessage("Von Datum muss im Format YYYY-MM angegeben werden.");
                form.von.setCustomValidity("VonDatum");
                correct = false;
            }
            else if (vonDate.isAfter(moment())) {
                setFromInvalidMessage("Von Datum darf nicht in der Zukunft liegen.");
                form.von.setCustomValidity("VonDatum");
                correct = false;
            }
            else {
                form.von.setCustomValidity("");
            }
            let bisDate = null;
            if (form.bis.value)
                bisDate = moment(form.bis.value);


            if (bisDate != null && !bisDate.isValid()) {
                setToInvalidMessage("Bis Datum muss im Format YYYY-MM angegeben werden oder leer sein, wenn Wohnsitz aktuell ist.");
                correct = false;
            }
            else if (bisDate != null && bisDate.isBefore(vonDate)) {
                setToInvalidMessage("Bis Datum muss nach Von-Datum liegen.");
                correct = false;
            }

            if (form.art.value === "-1") {
                setArtInvalidMessage("Bitte Art auswählen");
                form.art.setCustomValidity("Art");
                correct = false;
            }

            if (form.land.value === '') {
                form.land.setCustomValidity('Bitte Land auswählen');
                setLandInvalidity('Bitte Land auswählen');
                correct = false;
            }

            for (const feld of [form.organisation, form.taetigkeit]) {
                if (/keine\s*angabe/i.test(feld.value)) {
                    feld.setCustomValidity('„Keine Angaben" ist kein gültiger Eintrag — bitte konkret benennen');
                    correct = false;
                }
            }
            if (form.art.value === '2' && !/\bAMS\b/i.test(form.taetigkeit.value)) {
                form.taetigkeit.setCustomValidity('Bei „Arbeitslos" muss „beim AMS gemeldet" stehen. Nicht beim AMS gemeldet? Dann „Vereinsmitgliedschaft" wählen.');
                correct = false;
            }

            const arbeit: Arbeit = {
                art: form.art.value,
                organisation: form.organisation.value,
                taetigkeit: form.taetigkeit.value,
                land: form.land.value.toUpperCase(),
                von: vonDate.toDate(),
                bis: bisDate?.toDate()

            }
            if (correct)
                props.callbackOnAdd(arbeit);
        }

        setValidated(true);

    };

    // Kein Platzhalter „Keine Angaben" mehr (Fabian, 23.10.2026) — die Behörde
    // wies damit ab (Moosbrugger). Sinnvolle Vorbelegung je Art, der Rest muss
    // konkret benannt werden.
    // Arbeitslos heißt zwingend „beim AMS gemeldet"; wer weder Beschäftigung
    // noch AMS-Meldung hat, trägt als Minimal-Status die Vereinsmitgliedschaft
    // beim Flugsportzentrum Tirol ein (Fabian, 23.10.2026).
    const VORBELEGUNG: Record<string, { organisation: string; taetigkeit: string }> = {
        '2': { organisation: 'Arbeitslos', taetigkeit: AMS_TEXT },
        '5': { organisation: 'Flugsportzentrum Tirol', taetigkeit: 'Vereinsmitglied' },
        '6': { organisation: 'Pension', taetigkeit: 'Pensionist/in' },
    };
    const handleArtChange = (e: ChangeEvent<HTMLSelectElement>) => {
        resetFormValidity(e);
        const vor = VORBELEGUNG[e.target.value];
        const org = formRef.current!.organisation;
        const tae = formRef.current!.taetigkeit;
        const warVorbelegt = Object.values(VORBELEGUNG).some((v) => v.organisation === org.value) || /keine\s*angabe/i.test(org.value);
        if (vor) {
            org.value = vor.organisation;
            tae.value = vor.taetigkeit;
        } else if (warVorbelegt) {
            org.value = '';
            tae.value = '';
        }
    }


    const resetFormValidity = (e: ChangeEvent<HTMLElement>) => {

        (e.target as HTMLInputElement).setCustomValidity('');
        setValidated(false);
    }

    const formRef = useRef<HTMLFormElement>(null);

    const requiredIcon = <FaAsterisk style={{ color: 'red', paddingLeft: '2px' }} ></FaAsterisk>
    const rowClassName = 'py-2';
    return <Form ref={formRef} noValidate validated={validated} onSubmit={handleSubmit}>
        <Container fluid='md' >
            <Row className={rowClassName}>
                <Col className={rowClassName} xs={12} md={4}>
                    <Form.Group>
                        <Form.Label>Art{requiredIcon}</Form.Label>
                        <Form.Select name='art' required onChange={handleArtChange}>
                            <option value="-1">Art wählen</option>
                            <option value="0">Beschäftigungsverhältnis</option>
                            <option value="1">Aus- und Weiterbildung</option>
                            <option value="2">Arbeitslos</option>
                            <option value="3">Selbständig</option>
                            <option value="5">Vereinsmitgliedschaft</option>
                            <option value="6">Pension</option>
                        </Form.Select>
                        <Form.Text muted>Keine Beschäftigung und nicht beim AMS gemeldet? Dann „Vereinsmitgliedschaft" (Flugsportzentrum Tirol) wählen.</Form.Text>
                        <Form.Control.Feedback type="invalid" >
                            {artInvalidMessage}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col className={rowClassName} xs={12} md={4}>
                    <Form.Group>
                        <Form.Label>Organisation{requiredIcon}</Form.Label>
                        <Form.Control name='organisation' type='text' required></Form.Control>
                    </Form.Group>
                </Col>
                <Col className={rowClassName} xs={12} md={4}>
                    <Form.Group>
                        <Form.Label>Tätigkeit{requiredIcon}</Form.Label>
                        <Form.Control name='taetigkeit' type='text' required></Form.Control>
                    </Form.Group>
                </Col>
            </Row>
            <Row className={rowClassName}>
                <Col className={rowClassName} xs={12} md={12}>
                    <Form.Group>
                        <Form.Label>Land{requiredIcon}</Form.Label>
                        <Form.Select name='land' required onChange={resetFormValidity}>
                            <option value={''}>Bitte Land wählen</option>
                            {
                                Object.keys(StatesAlpha2List.ALPHA2CODES).map((key, index) =>
                                    <option value={key} key={key}>{StatesAlpha2List.ALPHA2CODES[key]}</option>
                                )
                            }
                        </Form.Select>
                        <Form.Control.Feedback type='invalid' >
                            {landInvalidity}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row className={rowClassName}>
                <Col className={rowClassName} xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>von{requiredIcon}</Form.Label>
                        <Form.Control name='von' type='date' placeholder='YYYY-MM-DD' min='1920-01-01' max={moment().format('YYYY-MM-DD')} required onChange={resetFormValidity} ></Form.Control>
                        <Form.Control.Feedback type="invalid" >
                            {fromInvalidMessage}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col className={rowClassName} xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>bis (bei aktueller Beschäftigung leer)</Form.Label>
                        <div className='d-flex gap-1'>
                            <Form.Control name='bis' type='date' placeholder='YYYY-MM-DD' min='1920-01-01' max={moment().format('YYYY-MM-DD')} onChange={resetFormValidity} ></Form.Control>
                            <Button variant='outline-secondary' title='Datum löschen (= aktuell)' className='flex-shrink-0' onClick={(e) => {
                                const feld = (e.target as HTMLElement).closest('div')?.querySelector('input[name="bis"]') as HTMLInputElement | null;
                                if (feld) { feld.value = ''; feld.setCustomValidity(''); }
                            }}>✕</Button>
                        </div>
                        <Form.Control.Feedback type="invalid" >
                            {toInvalidMessage}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row className={rowClassName}>
                <Col>
                    <Button onClick={props.callbackAbort} variant="secondary">Abbrechen</Button>
                </Col>
                <Col>
                    <Button type="submit"  variant="success" className="btn-lg" >Speichern</Button>
                </Col>
            </Row>
        </Container>
    </Form>

}